<script setup lang="ts">
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'
import { NOW_PLAYING_SEEK_SECONDS } from '../../composables/useNowPlayingMedia'

/** Pause/play and ±10 s buttons for media that must be watched or heard to the end. */
defineProps<{ kind: 'video' | 'audio' }>()

const { t, nowPlaying } = useDeckPlayerContext()
const {
  isPlaying: isNowPlayingMediaPlaying,
  toggle: toggleNowPlayingMedia,
  seek: seekNowPlayingMedia,
} = nowPlaying
</script>

<template>
  <div v-if="kind === 'video'" class="pfc-media-video-controls">
    <button
      type="button"
      class="pfc-media-seek-btn"
      :aria-label="t('common.rewind10')"
      @click="seekNowPlayingMedia(-NOW_PLAYING_SEEK_SECONDS)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12.5 6v12L4 12l8.5-6z" fill="currentColor" />
        <path d="M20.5 6v12L12 12l8.5-6z" fill="currentColor" />
      </svg>
    </button>
    <button
      type="button"
      class="pfc-media-video-toggle"
      :aria-pressed="!isNowPlayingMediaPlaying"
      :aria-label="isNowPlayingMediaPlaying ? t('common.pause') : t('common.play')"
      @click="toggleNowPlayingMedia"
    >
      <svg v-if="isNowPlayingMediaPlaying" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
        <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
      </svg>
      <svg v-else viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M8 5v14l11-7z" fill="currentColor" />
      </svg>
    </button>
    <button
      type="button"
      class="pfc-media-seek-btn"
      :aria-label="t('common.forward10')"
      @click="seekNowPlayingMedia(NOW_PLAYING_SEEK_SECONDS)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M11.5 6v12L3 12l8.5-6z"
          fill="currentColor"
          transform="translate(24,0) scale(-1,1)"
        />
        <path
          d="M19.5 6v12L11 12l8.5-6z"
          fill="currentColor"
          transform="translate(24,0) scale(-1,1)"
        />
      </svg>
    </button>
  </div>
  <div v-else class="pfc-media-audio-controls">
    <button
      type="button"
      class="pfc-media-seek-btn pfc-media-seek-btn--audio"
      :aria-label="t('common.rewind10')"
      @click="seekNowPlayingMedia(-NOW_PLAYING_SEEK_SECONDS)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12.5 6v12L4 12l8.5-6z" fill="currentColor" />
        <path d="M20.5 6v12L12 12l8.5-6z" fill="currentColor" />
      </svg>
    </button>
    <button
      type="button"
      class="pfc-media-audio-now-playing"
      :class="{ 'pfc-media-audio-now-playing--paused': !isNowPlayingMediaPlaying }"
      :aria-pressed="!isNowPlayingMediaPlaying"
      :aria-label="isNowPlayingMediaPlaying ? t('common.pause') : t('common.play')"
      @click="toggleNowPlayingMedia"
    >
      <svg
        class="pfc-media-audio-now-playing__icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M4 9v6h3.6l4.9 4.2V4.8L7.6 9H4z" fill="currentColor" />
        <path
          d="M15.3 8.7a5 5 0 0 1 0 6.6"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
        <path
          d="M17.6 6.2a8.6 8.6 0 0 1 0 11.6"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          opacity="0.5"
        />
      </svg>
      <div class="pfc-media-audio-now-playing__bars" aria-hidden="true">
        <span v-for="bar in 5" :key="bar" />
      </div>
    </button>
    <button
      type="button"
      class="pfc-media-seek-btn pfc-media-seek-btn--audio"
      :aria-label="t('common.forward10')"
      @click="seekNowPlayingMedia(NOW_PLAYING_SEEK_SECONDS)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M11.5 6v12L3 12l8.5-6z"
          fill="currentColor"
          transform="translate(24,0) scale(-1,1)"
        />
        <path
          d="M19.5 6v12L11 12l8.5-6z"
          fill="currentColor"
          transform="translate(24,0) scale(-1,1)"
        />
      </svg>
    </button>
  </div>
</template>

<style scoped src="./styles/playerMedia.css"></style>
